import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  ViewContainerRef,
  DestroyRef,
  OnDestroy,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SelectedWorkspaceStore } from './stores/selected-workspace.store';
import { provideComponentStore } from '@ngrx/component-store';
import { HeatmapValuesService } from './shared/services/heatmap-values.service';
import { GlobalLoaderService } from './global-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { TokenService } from './pages/auth/services/token.service';
import { WorkspaceStore } from './pages/workspace/data-access/workspace-companies.store';
import { WorkspaceBoardsStore } from './pages/workspace/data-access/workspace-boards.store';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { IntercomService } from './shared/services/intercom.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, withLatestFrom, Subject, takeUntil, distinctUntilChanged } from 'rxjs';
import { MetricService } from './core/services/metric.service';
import { UserJourneyEvent } from './core/services/user-journey.types';
import { CookieConsentService } from './services/cookie-consent.service';
import { fromEvent } from 'rxjs';
import { NotificationsService } from './shared/services/notifications.service';
import { AnnouncementsService } from './shared/services/announcements.service';
import { PdfExportContextService } from './core/services/pdf-export-context.service';
import { MetaPixelService } from './shared/services/meta-pixel.service';
import { GtmService } from './shared/services/gtm.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DecimalPipe,
    CurrencyPipe,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public title = 'perare-main';
  public currentLang = 'en';
  private destroyRef = inject(DestroyRef);
  private readonlyMetricService = inject(MetricService);

  private destroy$ = new Subject<void>();
  private announcementsChecked = false; // Защита от множественных проверок

  // Флаг для скрытия map-loader на определенных страницах
  public isWidgetsDemoPage = false;
  public isPdfExportPage = false;

  private pdfContext = inject(PdfExportContextService);
  private metaPixel = inject(MetaPixelService);
  private gtm = inject(GtmService);

  constructor(
    private selectedWorkspace: SelectedWorkspaceStore,
    private token: TokenService,
    public vcr: ViewContainerRef,
    private heatmapValues: HeatmapValuesService,
    public globalLoader: GlobalLoaderService,
    private translate: TranslateService,
    private intercomService: IntercomService,
    private metricService: MetricService,
    private cookieConsent: CookieConsentService,
    private router: Router,
    private notificationsService: NotificationsService,
    private announcementsService: AnnouncementsService,
  ) {
    translate.setDefaultLang('en');

    const currentLang = localStorage.getItem('current_lang');
    if (currentLang) {
      translate.use(currentLang);
      return;
    }

    translate.use('en');

    const browserLanguage = translate.getBrowserLang();

    if (browserLanguage) {
      if (browserLanguage === 'id' || browserLanguage === 'ind') {
        translate.use('id');
        localStorage.setItem('current_lang', 'id');
      }
    }
  }

  ngOnInit(): void {
    // Проверяем начальный роут
    this.isWidgetsDemoPage = this.router.url.includes('/widgets-demo');
    const initialIsDataOnlyRoute = this.router.url.includes('/dashboard-export-data/') ||
                                   this.router.url.includes('/polygon-print/');
    this.isPdfExportPage =
      this.router.url.includes('/dashboard-export/') ||
      this.router.url.includes('/dashboard-map/') ||
      this.router.url.includes('/dashboard-export-data/') ||
      this.router.url.includes('/polygon-print/');

    // Set data-only mode early if we're on the data-only route
    if (initialIsDataOnlyRoute) {
      this.pdfContext.setDataOnlyMode(true);
      this.pdfContext.setPdfMode(true);
    }

    // Отправляем метрику о текущем языке приложения
    const currentLang = this.translate.currentLang || this.translate.defaultLang || 'en';
    this.metricService.track({
      name: `language_selected_${currentLang}`
    });
    
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe((event) => {
      this.isWidgetsDemoPage = event.urlAfterRedirects.includes('/widgets-demo');
      const routeIsDataOnly =
        event.urlAfterRedirects.includes('/dashboard-export-data/') ||
        event.urlAfterRedirects.includes('/polygon-print/');
      this.isPdfExportPage =
        event.urlAfterRedirects.includes('/dashboard-export/') ||
        event.urlAfterRedirects.includes('/dashboard-map/') ||
        event.urlAfterRedirects.includes('/dashboard-export-data/') ||
        event.urlAfterRedirects.includes('/polygon-print/');

      if (routeIsDataOnly) {
        this.pdfContext.setDataOnlyMode(true);
        this.pdfContext.setPdfMode(true);
      } else {
        this.pdfContext.setDataOnlyMode(false);
      }

      if (!this.pdfContext.isDataOnlyMode) {
        this.metricService.track({ name: UserJourneyEvent.PAGE_VIEW });

        if (this.cookieConsent.analyticsAllowed()) {
          this.metaPixel.trackPageView(event.urlAfterRedirects);
        }
      }
    });

    this.cookieConsent.consentChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe((consent) => {
        if (consent.analytics) {
          this.initMarketingAnalytics(this.router.url);
        }
      });

    if (this.cookieConsent.analyticsAllowed()) {
      this.initMarketingAnalytics(this.router.url);
    }

    this.selectedWorkspace.user$
      .pipe(
        filter(
          (user): user is NonNullable<typeof user> => !!user && user.id !== 0,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((user) => {
        this.cookieConsent.syncFromUserConsents(user.consent);
        if (this.cookieConsent.analyticsAllowed()) {
          this.initMarketingAnalytics(this.router.url);
        }
      });

    // Skip user refresh in data-only mode (check both context and URL directly)
    const checkIsDataOnlyRoute = typeof window !== 'undefined' &&
      (window.location.href.includes('/dashboard-export-data/') ||
       window.location.href.includes('/polygon-print/') ||
       this.router.url.includes('/dashboard-export-data/') ||
       this.router.url.includes('/polygon-print/'));
    if (this.token.checkToken() && !this.pdfContext.isDataOnlyMode && !checkIsDataOnlyRoute) {
      this.selectedWorkspace.refreshUser();
    }

    // Проверяем анонсы после того, как пользователь и проект загружены
    this.selectedWorkspace.project$
      .pipe(
        filter((project) => !!project),
        withLatestFrom(this.selectedWorkspace.user$),
        filter(([project, user]) => !!project && !!user),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([project, user]) => {
        if (user && project && this.cookieConsent.analyticsAllowed()) {
          this.intercomService.boot({
            user_id: user.uuid,
            name: user.name,
            email: user.email,
            plan: project.subscriptionType,
          });
        }
        // Проверяем анонсы после загрузки проекта и пользователя (только один раз)
        if (!this.announcementsChecked) {
          this.announcementsChecked = true;
          this.checkAnnouncements();
        }
      });

    // Start polling notifications when project is selected
    this.selectedWorkspace.project$
      .pipe(
        filter((project) => !!project),
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        takeUntil(this.destroy$)
      )
      .subscribe((project) => {
        if (project && project.id) {
          this.notificationsService.startPolling(project.id);
        }
      });
  }

  private marketingAnalyticsInitialized = false;

  private initMarketingAnalytics(currentUrl: string): void {
    if (this.marketingAnalyticsInitialized || this.pdfContext.isDataOnlyMode) {
      return;
    }

    this.gtm.init();
    this.metaPixel.init();
    this.metaPixel.trackPageView(currentUrl);
    this.marketingAnalyticsInitialized = true;
  }

  /**
   * Проверить и показать последний анонс
   * Вызывается после проверки токена (когда пользователь авторизован)
   */
  private async checkAnnouncements(): Promise<void> {
    console.log('[App] Checking announcements...');
    // Не блокируем загрузку кабинета - проверка выполняется асинхронно
    this.announcementsService.checkAndShowLastAnnouncement().catch((error) => {
      console.error('[App] Failed to check announcements:', error);
    });
  }

  ngAfterViewInit() {
    const loader = document.querySelector('.loading-overlay-main');

    if (loader) {
      loader.remove();
      this.globalLoader.isLoading.set(true);
    }
  }

  ngOnDestroy(): void {
    this.notificationsService.stopPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
